import { reactive, computed } from 'vue'

/**
 * Composable for managing filter state
 * Handles filterObj, price validation, and space validation
 */
export const useFilterState = () => {
  // Main filter object state
  const filterObj = reactive({
    category_id: null,
    sort_type: 'desc',
    product_type: "",
    governorate_id: null,
    area_id: null,
    rating: "",
    min_price: null,
    max_price: null,
    is_negotiable: true,
    store_id: "",
    sort_by: "date",
    sort_by_force: '',
    min_space: null,
    max_space: null,
    attribute_ids: [],
  })

  // Price validation state
  const price = reactive({
    isErr: false,
    min_price: {
      val: "",
      err: ""
    },
    max_price: {
      val: "",
      err: ""
    },
  })

  // Space validation state
  const spaces = reactive({
    isErr: false,
    min_space: {
      val: "",
      err: ""
    },
    max_space: {
      val: "",
      err: ""
    },
  })

  /**
   * Reset all filters to default values
   * @param {Object} yearRangeLimits - Year range limits for resetting year attributes
   * @param {Array} allAttributes - All attributes for resetting checked states
   */
  const resetFilters = (yearRangeLimits = {}, allAttributes = []) => {
    filterObj.category_id = null
    filterObj.sort_type = ''
    filterObj.product_type = ''
    filterObj.governorate_id = null
    filterObj.area_id = null

    filterObj.min_price = null
    filterObj.max_price = null
    price.min_price.val = ""
    price.max_price.val = ""
    price.min_price.err = ""
    price.max_price.err = ""
    price.isErr = false

    filterObj.min_space = null
    filterObj.max_space = null
    spaces.min_space.val = ""
    spaces.max_space.val = ""
    spaces.min_space.err = ""
    spaces.max_space.err = ""
    spaces.isErr = false

    filterObj.is_negotiable = true

    filterObj.store_id = ''
    filterObj.sort_by = ''
    filterObj.sort_by_force = ''
    filterObj.rating = ""
    filterObj.attribute_ids = []

    // Reset year ranges to full range for all year attributes
    Object.keys(yearRangeLimits).forEach(parentId => {
      const limits = yearRangeLimits[parentId];
      if (limits) {
        // Note: Year range values are managed in useYearRange composable
        // This is just for clearing attribute_ids
      }
    });

    // Clear all attributes and reset their checked state
    if (Array.isArray(allAttributes)) {
      allAttributes.forEach(parent => {
        if (parent.children) {
          parent.children.forEach(child => {
            child.isChecked = false
            child.isNestedOpen = false
            
            if (child.attributes && Array.isArray(child.attributes)) {
              child.attributes.forEach(nestedAttr => {
                nestedAttr.isNestedChildrenOpen = false
                
                if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                  nestedAttr.children.forEach(nestedChild => {
                    nestedChild.isChecked = false
                  })
                }
              })
            }
          })
        }
      })
    }
  }

  /**
   * Validate price range inputs
   * @returns {boolean} - True if validation passes
   */
  const validatePrice = () => {
    if (price.min_price.val >= price.max_price.val && price.min_price.val != "" && !price.max_price.val) {
      price.isErr = true
      return false
    } else {
      price.isErr = false
      filterObj.max_price = price.max_price.val
      filterObj.min_price = price.min_price.val
      return true
    }
  }

  /**
   * Validate space range inputs
   * @returns {boolean} - True if validation passes
   */
  const validateSpaces = () => {
    if (spaces.min_space.val >= spaces.max_space.val && spaces.min_space.val != "" && !spaces.max_space.val) {
      spaces.isErr = true
      return false
    } else {
      spaces.isErr = false
      filterObj.max_space = spaces.max_space.val
      filterObj.min_space = spaces.min_space.val
      return true
    }
  }

  /**
   * Update filter object with new values
   * @param {Object} updates - Object with properties to update
   */
  const updateFilterObj = (updates) => {
    Object.keys(updates).forEach(key => {
      if (key in filterObj) {
        filterObj[key] = updates[key]
      }
    })
  }

  /**
   * Set price values from external source (e.g., URL params)
   * @param {number|string} min - Minimum price
   * @param {number|string} max - Maximum price
   */
  const setPriceValues = (min, max) => {
    if (min !== null && min !== undefined) {
      price.min_price.val = min
      filterObj.min_price = min
    }
    if (max !== null && max !== undefined) {
      price.max_price.val = max
      filterObj.max_price = max
    }
  }

  /**
   * Set space values from external source (e.g., URL params)
   * @param {number|string} min - Minimum space
   * @param {number|string} max - Maximum space
   */
  const setSpaceValues = (min, max) => {
    if (min !== null && min !== undefined) {
      spaces.min_space.val = min
      filterObj.min_space = min
    }
    if (max !== null && max !== undefined) {
      spaces.max_space.val = max
      filterObj.max_space = max
    }
  }

  return {
    filterObj,
    price,
    spaces,
    resetFilters,
    validatePrice,
    validateSpaces,
    updateFilterObj,
    setPriceValues,
    setSpaceValues,
  }
}

